import React, { useEffect, useState } from 'react'
import {
  Box,
  // Card,
  makeStyles,
  Tabs,
  Tab,
  Typography,
  Grid,
  FormControlLabel,
  Switch,
  TextField
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { fetchSettings, updateSettingById } from 'store/settings'
import Wrapper from 'components/Wrapper'
import { Stack } from '@mui/material'
import TemplateCard from 'components/TemplateCard'
import ResourceNotFound from 'components/ResourceNotFound'
import { toast } from 'react-toastify'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import { Form, Formik } from 'formik'
import ProgressButton from 'components/ProgressButton'
import { updateConferenceById } from 'store/conference'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { generalSettingsFormSchema } from 'schemas/setting'
import moment from 'moment/moment'
import ReactQuill from 'react-quill'
import { modules } from 'views/admin/settings/SettingForm/TemplateForm'
import ErrorMessage from 'components/ErrorMessage'

const TemplateNames = {
  exhibitorInvitationEmail: { id: 'exhibitorInvitationEmail', name: 'Exhibitor Invitation Email' },
  exhibitorReminderEmail: { id: 'exhibitorReminderEmail', name: 'Exhibitor Reminder Email' },
  exhibitorConfirmationEmail: {
    id: 'exhibitorConfirmationEmail',
    name: 'Exhibitor Confirmation Email'
  },
  exhibitorUpdateInfoEmail: {
    id: 'exhibitorUpdateInfoEmail',
    name: 'Exhibitor Data Imported Email'
  },
  speakerInvitationEmail: { id: 'speakerInvitationEmail', name: 'Speaker Invitation Email' },
  speakerReminderEmail: { id: 'speakerReminderEmail', name: 'Speaker Reminder Email' },
  speakerConfirmationEmail: { id: 'speakerConfirmationEmail', name: 'Speaker Confirmation Email' },
  speakerReminderEmailTalkSubmissions: {
    id: 'speakerReminderEmailTalkSubmissions',
    name: 'Speaker Reminder Email for Talk Submissions'
  },
  speakerUpdateInfoEmail: {
    id: 'speakerUpdateInfoEmail',
    name: 'Speaker Data Imported Email'
  },

  sponsorInvitationEmail: { id: 'sponsorInvitationEmail', name: 'Sponsor Invitation Email' },
  sponsorReminderEmail: { id: 'sponsorReminderEmail', name: 'Sponsor Reminder Email' },
  sponsorConfirmationEmail: { id: 'sponsorConfirmationEmail', name: 'Sponsor Confirmation Email' },
  sponsorUpdateInfoEmail: {
    id: 'sponsorUpdateInfoEmail',
    name: 'Sponsor Data Imported Email'
  },

  exportLeadsEmail: { id: 'exportLeadsEmail', name: 'Export Leads Email' },
  attendeeInvitationEmail: { id: 'attendeeInvitationEmail', name: 'Attendee Invitation Email' },
  attendeeReminderEmail: { id: 'attendeeReminderEmail', name: 'Attendee Reminder Email' }
}

const templateTypes = {
  exhibitor: {
    id: 'exhibitor',
    label: 'Exhibitor',
    items: [
      TemplateNames.exhibitorInvitationEmail,
      TemplateNames.exhibitorReminderEmail,
      TemplateNames.exhibitorConfirmationEmail,
      TemplateNames.exhibitorUpdateInfoEmail
    ]
  },
  speaker: {
    id: 'speaker',
    label: 'Speaker',
    items: [
      TemplateNames.speakerInvitationEmail,
      TemplateNames.speakerReminderEmail,
      TemplateNames.speakerConfirmationEmail,
      TemplateNames.speakerUpdateInfoEmail,
      TemplateNames.speakerReminderEmailTalkSubmissions
    ]
  },
  sponsor: {
    id: 'sponsor',
    label: 'Sponsor',
    items: [
      TemplateNames.sponsorInvitationEmail,
      TemplateNames.sponsorReminderEmail,
      TemplateNames.sponsorConfirmationEmail,
      TemplateNames.sponsorUpdateInfoEmail
    ]
  },
  attendee: {
    id: 'attendee',
    label: 'Attendee',
    items: [
      TemplateNames.attendeeInvitationEmail,
      TemplateNames.attendeeReminderEmail,
      TemplateNames.exportLeadsEmail
    ]
  }
}

export const SettingForm = () => {
  const { user } = useSelector((state) => state.auth)
  const dispatch = useDispatch()
  const classes = useStyles()
  const { conference } = useSelector((state) => state.conference)
  const { settings, inProgress } = useSelector((state) => state.setting)
  const [settingTab, setSettingTab] = useState('general')
  const [value, setValue] = useState(templateTypes.exhibitor.id)
  const [isLoading, setIsLoading] = useState(false)

  const generalSettingsInitialValues = {
    speakerCanUploadTalks: conference?.speakerCanUploadTalks || false,
    enableAttendeeToChangeTimezone: conference?.enableAttendeeToChangeTimezone || false,
    enableAttendance: conference?.enableAttendance || false,
    attendanceStartTime: conference?.attendanceStartTime
      ? moment(conference?.attendanceStartTime, 'hh:mm A')
      : '',
    attendanceEndTime: conference?.attendanceEndTime
      ? moment(conference?.attendanceEndTime, 'hh:mm A')
      : '',
    attendanceInfoMessage: conference?.attendanceInfoMessage || ''
  }

  const handleSubmit = async (values) => {
    try {
      startLoading()
      const payload = {
        speakerCanUploadTalks: values.speakerCanUploadTalks,
        enableAttendance: values.enableAttendance,
        enableAttendeeToChangeTimezone: values.enableAttendeeToChangeTimezone,
        attendanceStartTime: values.enableAttendance
          ? moment(values.attendanceStartTime).format('hh:mm A')
          : null,
        attendanceEndTime: values.enableAttendance
          ? moment(values.attendanceEndTime).format('hh:mm A')
          : null,
        attendanceInfoMessage: values.enableAttendance ? values?.attendanceInfoMessage?.trim() : ''
      }
      await dispatch(updateConferenceById(conference?.id, payload))
      toast.success('Settings updated successfully')
      stopLoading()
    } catch (err) {
      toast.error('There was some problem updating your settings. Please try again')
      stopLoading()
      // do nothing
    }
  }
  const handleChange = (event, newValue) => {
    setValue(newValue)
  }

  const handleChangeSettingTab = (event, newValue) => {
    setSettingTab(newValue)
  }

  const startLoading = () => setIsLoading(true)

  const stopLoading = () => setIsLoading(false)

  useEffect(() => {
    dispatch(fetchSettings(user?.conferenceId))
  }, [user?.conferenceId])

  const onSaveTemplate = (newValues, template, closeModal) => {
    if (user?.conferenceId) {
      startLoading()
      dispatch(
        updateSettingById(
          user?.conferenceId,
          {
            [template?.id]: {
              subject: newValues?.subject?.trim(),
              body: newValues?.body?.trim()
            }
          },
          () => {
            toast.success('Successfully saved')
            closeModal()
            stopLoading()
          },
          () => {
            toast.error('Failed to save the template. Please try again.')
            stopLoading()
          }
        )
      )
    }
  }

  return (
    <Box>
      <Box className={classes.headBar}>
        <Typography variant="h4">Settings</Typography>
        <Stack justifyContent="center" alignItems="center">
          <Tabs
            value={settingTab}
            onChange={handleChangeSettingTab}
            variant="scrollable"
            scrollButtons="off"
            className={classes.tabs}>
            <Tab label="General" value="general" icon={<SettingsOutlinedIcon />} />
            <Tab label="Email Templates" value="email" icon={<MailOutlineIcon />} />
          </Tabs>
        </Stack>
      </Box>

      <Box sx={{ marginTop: '1rem' }}>
        {settingTab === 'general' && (
          <div className={classes.card}>
            <Formik
              enableReinitialize={true}
              initialValues={generalSettingsInitialValues}
              validationSchema={generalSettingsFormSchema}
              onSubmit={handleSubmit}>
              {({ setFieldValue, values, errors, touched }) => (
                <Form>
                  <Box className={classes.header}>
                    <Typography className={classes.title}>General Settings</Typography>
                  </Box>
                  <Grid container spacing={2} marginTop="1rem">
                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        labelPlacement="start"
                        label={
                          <Typography className={classes.label}>
                            Allow speakers to upload their talks
                          </Typography>
                        }
                        control={
                          <Switch
                            className={classes.switch}
                            name="speakerCanUploadTalks"
                            onChange={(e) =>
                              setFieldValue('speakerCanUploadTalks', e.target.checked)
                            }
                            checked={values.speakerCanUploadTalks}
                          />
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        labelPlacement="start"
                        label={
                          <Typography className={classes.label}>
                            Allow Attendee to change timezone
                          </Typography>
                        }
                        control={
                          <Switch
                            className={classes.switch}
                            name="enableAttendeeToChangeTimezone"
                            onChange={(e) =>
                              setFieldValue('enableAttendeeToChangeTimezone', e.target.checked)
                            }
                            checked={values.enableAttendeeToChangeTimezone}
                          />
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControlLabel
                        labelPlacement="start"
                        label={
                          <Typography className={classes.label}>
                            Enable Education Credit module
                          </Typography>
                        }
                        control={
                          <Switch
                            className={classes.switch}
                            name="enableAttendance"
                            onChange={(e) => setFieldValue('enableAttendance', e.target.checked)}
                            checked={values.enableAttendance}
                          />
                        }
                      />
                    </Grid>

                    {values.enableAttendance && (
                      <Grid item xs={12}>
                        <Box className={classes.attendanceModule}>
                          <Box sx={{ mb: 1 }}>
                            <Typography className={classes.headingLabel}>
                              Attendance Check-In Time slot
                            </Typography>
                            <Typography variant="body2" className={classes.subText}>
                              Attendees can mark attendance during this time slot only
                            </Typography>
                          </Box>
                          <LocalizationProvider dateAdapter={AdapterMoment}>
                            <Grid container spacing={2} marginTop="1rem">
                              <Grid item xs={6}>
                                <TimePicker
                                  ampm={!conference?.is24hrFormat}
                                  name="attendanceStartTime"
                                  value={values.attendanceStartTime}
                                  label="Start Time"
                                  onChange={(value) => setFieldValue('attendanceStartTime', value)}
                                  closeOnSelect={false}
                                  renderInput={({ inputProps, ...rest }) => (
                                    <TextField
                                      className={classes.timePicker}
                                      {...rest}
                                      variant="outlined"
                                      helperText={
                                        touched.attendanceStartTime && errors.attendanceStartTime
                                          ? errors.attendanceStartTime
                                          : null
                                      }
                                      error={Boolean(
                                        touched.attendanceStartTime && errors.attendanceStartTime
                                      )}
                                      inputProps={{
                                        ...inputProps,
                                        placeholder: `hh:mm ${
                                          !conference?.is24hrFormat ? 'A' : ''
                                        }`,
                                        readOnly: true
                                      }}
                                      InputLabelProps={{
                                        shrink: true
                                      }}
                                    />
                                  )}
                                />
                              </Grid>

                              <Grid item xs={6}>
                                <TimePicker
                                  ampm={!conference?.is24hrFormat}
                                  name="attendanceEndTime"
                                  value={values.attendanceEndTime}
                                  label="End Time"
                                  onChange={(value) => setFieldValue('attendanceEndTime', value)}
                                  closeOnSelect={false}
                                  renderInput={({ inputProps, ...rest }) => (
                                    <TextField
                                      {...rest}
                                      className={classes.timePicker}
                                      variant="outlined"
                                      helperText={
                                        touched.attendanceEndTime && errors.attendanceEndTime
                                          ? errors.attendanceEndTime
                                          : null
                                      }
                                      error={Boolean(
                                        touched.attendanceEndTime && errors.attendanceEndTime
                                      )}
                                      inputProps={{
                                        ...inputProps,
                                        placeholder: `hh:mm ${
                                          !conference?.is24hrFormat ? 'A' : ''
                                        }`,
                                        readOnly: true
                                      }}
                                      InputLabelProps={{
                                        shrink: true
                                      }}
                                    />
                                  )}
                                />
                              </Grid>
                            </Grid>
                          </LocalizationProvider>

                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <Box sx={{ mb: 1 }}>
                              <Typography className={classes.headingLabel}>
                                Education Credit Explanation Message
                              </Typography>
                              <Typography variant="body2" className={classes.subText}>
                                This text appears as helpful guidelines for attendees when marking
                                their attendance
                              </Typography>
                            </Box>
                            <Grid item xs={12}>
                              <Box
                                sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <ReactQuill
                                  theme="snow"
                                  modules={modules}
                                  value={values.attendanceInfoMessage}
                                  onChange={(value) =>
                                    setFieldValue('attendanceInfoMessage', value)
                                  }
                                  placeholder="Write attendance info message here..."
                                />
                                <ErrorMessage
                                  message={
                                    touched?.attendanceInfoMessage && errors?.attendanceInfoMessage
                                  }
                                />
                              </Box>
                            </Grid>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                  <Stack direction="row" justifyContent="flex-end" marginTop={2}>
                    <ProgressButton
                      type="submit"
                      label="Save"
                      inProgress={isLoading}
                      disabled={isLoading}
                    />
                  </Stack>
                </Form>
              )}
            </Formik>
          </div>
        )}
        {settingTab === 'email' && (
          <>
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="off"
              className={classes.tabs}>
              {Object.keys(templateTypes).map((key) => (
                <Tab
                  label={templateTypes[key]?.label}
                  key={templateTypes[key]?.id}
                  value={templateTypes[key]?.id}
                />
              ))}
            </Tabs>

            <Wrapper inProgress={inProgress}>
              {value && templateTypes?.[value] ? (
                <Stack gap={2} mt={2} className={classes.cards}>
                  {templateTypes[value].items
                    .map((item) => ({
                      name: item?.name,
                      subject: settings?.[item?.id]?.subject || '',
                      body: settings?.[item?.id]?.body || '',
                      id: item?.id
                    }))
                    .map((item) => (
                      <TemplateCard
                        key={item.id}
                        id={item.id}
                        template={item}
                        isLoading={isLoading}
                        onSaveTemplate={(values, closeModal) =>
                          onSaveTemplate(values, item, closeModal)
                        }
                      />
                    ))}
                </Stack>
              ) : (
                <ResourceNotFound message="No email templates available" />
              )}
            </Wrapper>
          </>
        )}
      </Box>
    </Box>
  )
}

export default SettingForm

const useStyles = makeStyles((theme) => ({
  headBar: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '1rem',
    zIndex: 999,
    position: 'sticky',
    top: '98px',
    background: theme.palette.background.paper
  },
  subText: {
    marginTop: '2px',
    color: '#979797',
    lineHeight: '10px'
  },
  tabs: {
    '& .MuiTab-root.MuiTab-labelIcon': {
      minHeight: '35px'
    },
    '& .MuiTab-root svg': {
      height: '20px',
      width: '20px',
      marginBottom: '0px !important'
    },
    '& .MuiTab-root .MuiTab-wrapper': {
      display: 'flex',
      flexDirection: 'row',
      gap: '8px',
      alignItems: 'center'
    },
    '& .MuiTab-root.Mui-selected': {
      color: theme.palette.primary.main
    }
  },
  card: {
    padding: '1rem',
    border: '1px solid #E0E0E0',
    borderRadius: '8px'
  },
  label: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#4A4A4A',
    letterSpacing: '0.5px',
    marginRight: '1rem'
  },
  header: {
    background: '#CAEDF5',
    padding: '12px',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    marginBottom: '1rem'
  },
  title: {
    color: 'rgb(66, 66, 66)',
    fontWeight: 600,
    fontSize: '16px',
    letterSpacing: '0.8px'
  },
  attendanceModule: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    border: '1px solid #E0E0E0',
    borderRadius: '8px',
    padding: '16px'
  },
  headingLabel: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: '16px',
    letterSpacing: '0.8px'
  },
  timePicker: {
    width: '100%'
  }
}))
